import React, { useState, useRef } from "react";
import { NavLink } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import "./BannerCookie.scss";
import Icon from "../../components/Icon/Icon";
import { RootState, AppDispatch } from "../../redux/store";
import { setAnalytics, setPreference, setPrivacy } from "../../redux/cookieSlice";
import { initAnalytics, trackPageView } from "../../util/analytics";

export default function BannerCookie(): JSX.Element {
  const [preferenceOpen, setPreferenceOpen] = useState(false);
  const dispatch: AppDispatch = useDispatch();
  const { analytics, preference, privacy } = useSelector(
    (state: RootState) => state.cookie,
  );
  const cookieRef = useRef<HTMLDivElement>(null);

  function savePreferences(newAnalytics: number, newPreference: number) {
    localStorage.setItem("cookieAnalytics", newAnalytics.toString());
    localStorage.setItem("cookiePreference", newPreference.toString());
    localStorage.setItem("cookiePrivacy", "1");

    dispatch(setPrivacy(1));
    dispatch(setPreference(newPreference));
    dispatch(setAnalytics(newAnalytics));

    if (newAnalytics === 1) {
      initAnalytics();
      trackPageView();
    }

    if (cookieRef.current) {
      cookieRef.current.style.display = "none";
    }
  }

  if (privacy) {
    return <></>;
  }

  return (
    <div
      className="banner-cookie-container"
      ref={cookieRef}
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-description"
    >
      <div className="cookie-banner">
        <div className="cookie-banner__accent" aria-hidden="true" />
        <div className="cookie-banner__header">
          <p className="cookie-banner__eyebrow">Privacy</p>
          <h2 id="cookie-banner-title" className="cookie-banner__title">
            We use cookies
          </h2>
        </div>

        <p id="cookie-banner-description" className="cookie-banner__text">
          We use cookies and similar technologies to personalise content and
          analyse website performance. Read our{" "}
          <NavLink
            to="/policies/cookies-policy"
            className="cookie-banner__link"
            onClick={() => setPreferenceOpen(false)}
          >
            Cookie Policy
          </NavLink>
          .
        </p>

        <div className="cookie-banner__actions">
          <button
            type="button"
            className="cookie-btn cookie-btn--primary"
            onClick={() => savePreferences(1, 1)}
          >
            <span>Accept all</span>
          </button>
          <button
            type="button"
            className="cookie-btn cookie-btn--secondary"
            onClick={() => savePreferences(0, 0)}
          >
            <span>Reject all</span>
          </button>
          <button
            type="button"
            className="cookie-btn cookie-btn--ghost"
            onClick={() => setPreferenceOpen(!preferenceOpen)}
            aria-expanded={preferenceOpen}
          >
            <span>Manage preferences</span>
            <Icon name={preferenceOpen ? "expand_less" : "expand_more"} size={18} />
          </button>
        </div>
      </div>

      <div
        className={`manage-cookies-container ${preferenceOpen ? "open" : ""}`}
        aria-hidden={!preferenceOpen}
      >
        <ul className="manage-cookies">
          <li className="manage-cookies__item">
            <div className="manage-cookies__row">
              <div>
                <h3 className="manage-cookies__label">Necessary</h3>
                <p className="manage-cookies__status">Always active</p>
              </div>
              <span className="manage-cookies__badge">Required</span>
            </div>
            <p className="manage-cookies__description">
              Required for core site features such as secure access and form
              submissions.
            </p>
          </li>

          <li className="manage-cookies__item">
            <div className="manage-cookies__row">
              <div>
                <h3 className="manage-cookies__label">Analytics</h3>
                <p className="manage-cookies__status">Usage insights</p>
              </div>
              <Toggle
                checked={analytics === 1}
                setChecked={(value) => dispatch(setAnalytics(value))}
                label="Analytics cookies"
              />
            </div>
            <p className="manage-cookies__description">
              Help us understand how visitors use the site, including traffic
              sources and page performance.
            </p>
          </li>

          <li className="manage-cookies__item">
            <div className="manage-cookies__row">
              <div>
                <h3 className="manage-cookies__label">Preference</h3>
                <p className="manage-cookies__status">Personalisation</p>
              </div>
              <Toggle
                checked={preference === 1}
                setChecked={(value) => dispatch(setPreference(value))}
                label="Preference cookies"
              />
            </div>
            <p className="manage-cookies__description">
              Remember your settings to provide a more tailored browsing
              experience.
            </p>
          </li>
        </ul>

        <button
          type="button"
          className="cookie-btn cookie-btn--primary cookie-btn--full"
          onClick={() => savePreferences(analytics, preference)}
        >
          <span>Save preferences</span>
        </button>
      </div>
    </div>
  );
}

interface ToggleProps {
  checked: boolean;
  setChecked: (value: number) => void;
  label: string;
}

function Toggle({ checked, setChecked, label }: ToggleProps): JSX.Element {
  return (
    <button
      type="button"
      className={`cookie-toggle ${checked ? "cookie-toggle--on" : ""}`}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => setChecked(checked ? 0 : 1)}
    >
      <span className="cookie-toggle__knob" />
    </button>
  );
}
